import { StyleSheet } from 'react-native';

import { webTapFloor } from '../../theme';
import type { useTheme } from '../theme';

// Logger header chrome + post-save action buttons (Track R — split from loggerStyles.ts; pure data,
// merged by the loggerStyles barrel). Not imported directly.
export function headerStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    flex: { flex: 1 },
    // GestureHandlerRootView wrapper inside the Modal (1.68.1). Needs
    // flex: 1 so the Modal's native tree fills the page-sheet area.
    gestureRoot: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      flex: 1,
      justifyContent: 'flex-start',
    },
    // Takes all the room up to the right cluster; a long status wraps to a
    // second line there instead of truncating.
    titleCol: {
      alignItems: 'flex-start',
      flex: 1,
      minWidth: 0,
    },
    // #336 S2: «Info» and the deload round's badge on one line.
    infoRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
    // #318: «Info», the field that opens the coach notes, as wide as its label.
    programLineBtn: {
      alignSelf: 'flex-start',
      marginTop: s.s1,
      marginRight: s.s2,
    },
    title: {
      alignSelf: 'stretch',
      color: p.ink,
      fontSize: t.sizes.md,
      fontWeight: '600',
    },
    rightActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    // Header icon button: shared shape for ⌄ minimize /
    // 💾 save / ✕ close. Square 36 box on native (oversized-UI shrink), where
    // LoggerHeader's hitSlop={8} keeps the touch target ≥44. Web ignores
    // hitSlop, so it gets the webTapFloor box instead. Glyph fontSize lg so
    // the 4-icon cluster doesn't fight for legibility on long-name workouts.
    // Each icon variant overrides only the glyph color.
    headerIconButton: {
      width: webTapFloor ?? 36,
      height: webTapFloor ?? 36,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: r.r2,
    },
    // "Lit" state of the manual-rest clock inside a superset (MX-5 D9). The
    // border + tint are IconButton's own `active` pair (`ink2` / `brand100`),
    // borrowed rather than re-picked so the two encodings of "active" can't
    // drift apart on colour — the box itself is what differs, because this
    // header's icons are bare and the primitive's are boxed.
    // ⚠️ Never colour alone: it is paired with accessibilityState.selected at
    // the callsite.
    headerIconButtonActive: {
      borderWidth: 1,
      borderColor: p.ink2,
      backgroundColor: p.brand100,
    },
    headerIconButtonPressed: {
      backgroundColor: p.surface2,
    },
    headerIconGlyph: {
      fontSize: t.sizes.lg,
      fontWeight: '700',
      lineHeight: t.sizes.lg,
      color: p.ink,
    },
    headerIconGlyphDanger: {
      color: p.ink2,
    },
    // Coach-notes panel, read-only, drops below the header from the program line.
    // Brand left-rail so it reads as authored guidance, not chrome. Height-
    // capped + scrollable so a long program description can't push the
    // logger body off-screen.
    notesPanel: {
      marginHorizontal: s.s5,
      marginBottom: s.s3,
      padding: s.s4,
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      borderLeftWidth: 3,
      borderLeftColor: p.ink3,
    },
    notesPanelScroll: {
      maxHeight: 220,
    },
    notesPanelScrollBody: {
      gap: s.s3,
    },
    notesPanelSection: {
      gap: s.s1,
    },
    // Collapsed-by-default accordion header — tap to reveal the note body.
    // ≥44pt row (a11y); chevron flips ▸→▾ as the second (non-colour) cue.
    notesAccordionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 44,
      paddingVertical: s.s2,
      gap: s.s3,
    },
    notesAccordionChevron: {
      color: p.ink3,
      fontSize: t.sizes.md,
      fontWeight: '700',
    },
    notesPanelLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    // #318: the plan's or program's name, heading the panel.
    notesPanelTitle: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    notesPanelText: {
      color: p.ink,
      fontSize: t.sizes.base,
      lineHeight: t.sizes.base + 6,
    },
    // #182: the MAX the save proposed, one line of words under the summary.
    postSaveMaxLine: {
      color: p.ink,
      fontSize: t.sizes.base,
      marginTop: s.s2,
    },
    postSaveActions: {
      flexDirection: 'row',
      gap: s.s3,
      marginTop: s.s2,
    },
    postSaveShareBtn: {
      flex: 1,
      paddingVertical: s.s3,
      borderRadius: r.r2,
      backgroundColor: p.brand500,
      alignItems: 'center',
      justifyContent: 'center',
    },
    postSaveShareText: {
      color: p.inkOnBrand,
      fontSize: t.sizes.md,
      fontWeight: '700',
      letterSpacing: 0.4,
    },
    postSaveDoneBtn: {
      flex: 1,
      paddingVertical: s.s3,
      borderRadius: r.r2,
      backgroundColor: p.surface2,
      borderWidth: 1,
      borderColor: p.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    postSaveDoneText: {
      color: p.ink,
      fontSize: t.sizes.md,
      fontWeight: '700',
      letterSpacing: 0.4,
    },
    postSaveBtnPressed: {
      opacity: 0.85,
    },
    postSaveBtnDisabled: {
      opacity: 0.6,
    },
  });
}
